import dayjs from "dayjs";
import { Trans } from "@coworker/locales";
import {
  MonthlyRecurrenceType,
  Recurrence,
  RecurrencePeriod,
  WeeklyRecurrenceDay,
} from "@coworker/apprestructured/src/task-management/types/Schedule";

export enum ScheduleType {
  NO_REPEAT = "no_repeat",
  FUTURE = "future",
  REPEATING = "repeating",
}

type Weekday = {
  weekday: string;
  weekdayPlural: string;
  shortFormattedWeekday: string;
  weekdayNumber: WeeklyRecurrenceDay;
};

export const getWeekdays = (t: (key: string) => string): Weekday[] => [
  {
    weekday: t("mondayString"),
    weekdayPlural: t("mondaysString"),
    shortFormattedWeekday: t("mondayString").slice(0, 3),
    weekdayNumber: 0,
  },
  {
    weekday: t("tuesdayString"),
    weekdayPlural: t("tuesdaysString"),
    shortFormattedWeekday: t("tuesdayString").slice(0, 3),
    weekdayNumber: 1,
  },
  {
    weekday: t("wednesdayString"),
    weekdayPlural: t("wednesdaysString"),
    shortFormattedWeekday: t("wednesdayString").slice(0, 3),
    weekdayNumber: 2,
  },
  {
    weekday: t("thursdayString"),
    weekdayPlural: t("thursdaysString"),
    shortFormattedWeekday: t("thursdayString").slice(0, 3),
    weekdayNumber: 3,
  },
  {
    weekday: t("fridayString"),
    weekdayPlural: t("fridaysString"),
    shortFormattedWeekday: t("fridayString").slice(0, 3),
    weekdayNumber: 4,
  },
  {
    weekday: t("saturdayString"),
    weekdayPlural: t("saturdaysString"),
    shortFormattedWeekday: t("saturdayString").slice(0, 3),
    weekdayNumber: 5,
  },
  {
    weekday: t("sundayString"),
    weekdayPlural: t("sundaysString"),
    shortFormattedWeekday: t("sundayString").slice(0, 3),
    weekdayNumber: 6,
  },
];

export function getWeekdayNumber(date: string): WeeklyRecurrenceDay {
  // In dayjs, 0 means Sunday. We want 0 to mean Monday.
  return ((dayjs(date).day() + 6) % 7) as WeeklyRecurrenceDay;
}

export function calculateWeekdayOrdinalTranslationKey(dateString: string) {
  const date = dayjs(dateString);
  switch (Math.floor((date.date() - 1) / 7)) {
    case 0: {
      return "ordinalfirstString";
    }
    case 1: {
      return "ordinalsecondString";
    }
    case 2: {
      return "ordinalthirdString";
    }
    case 3:
    default: {
      return "ordinalfourthString";
    }
  }
}

function formatDateWithTranslatedMonth(
  date: string,
  t: (key: string) => string
) {
  const d = dayjs(date);
  const monthName = d.format("MMMM");
  const day = d.date();
  const year = d.year();

  const monthTransKey = `${monthName.toLowerCase()}String`;
  const translatedMonth = t(monthTransKey);

  return `${translatedMonth} ${day}, ${year}`;
}

function formatWeekdaysList(
  days: WeeklyRecurrenceDay[],
  t: (key: string) => string,
  usePlural: boolean = true
): string {
  const weekdays = getWeekdays(t);

  const weekdayNames = days
    .sort((a, b) => a - b)
    .map((weekdayNumber) => {
      const weekday = weekdays.find((d) => d.weekdayNumber === weekdayNumber);
      return usePlural ? weekday?.weekdayPlural : weekday?.weekday;
    });

  if (weekdayNames.length === 1) {
    return weekdayNames[0] || "";
  } else if (weekdayNames.length === 2) {
    return `${weekdayNames[0]} ${t("andString")} ${weekdayNames[1]}`;
  } else if (weekdayNames.length > 2) {
    return (
      weekdayNames.slice(0, -1).join(", ") +
      ` ${t("andString")} ` +
      weekdayNames[weekdayNames.length - 1]
    );
  }

  return "";
}

function getMonthlyRecurrenceText(
  monthlyRecurrenceType: MonthlyRecurrenceType,
  startDate: string,
  t: (key: string) => string
): string {
  const weekdays = getWeekdays(t);

  const startWeekdayNumber = getWeekdayNumber(startDate);
  const startDateWeekday = weekdays.find(
    (day) => day.weekdayNumber === startWeekdayNumber
  );

  if (monthlyRecurrenceType === "last_weekday") {
    return `${t("theString")} ${t("lastString")} ${startDateWeekday?.weekday}`;
  } else {
    return `${t("theString")} ${t(calculateWeekdayOrdinalTranslationKey(startDate))} ${startDateWeekday?.weekday}`;
  }
}

export function futureTaskSummaryText(
  start: string,
  t: (key: string, params?: Record<string, string | number>) => string
) {
  const startDate = formatDateWithTranslatedMonth(start, t);

  return (
    <Trans
      values={{
        start: startDate,
      }}
    >
      futureTaskSummaryWithoutDueText
    </Trans>
  );
}

export function repeatingTaskSummaryText(
  recurrencePeriod: RecurrencePeriod | null,
  start: string,
  end: string,
  weeklyRecurrenceDays: WeeklyRecurrenceDay[],
  monthlyRecurrenceType: MonthlyRecurrenceType,
  t: (key: string, params?: Record<string, string | number>) => string
) {
  const startDate = formatDateWithTranslatedMonth(start, t);
  const endDate = formatDateWithTranslatedMonth(end, t);

  switch (recurrencePeriod) {
    case "daily":
      return (
        <Trans values={{ start: startDate, end: endDate }}>
          dailyRepeatingTaskSummaryText
        </Trans>
      );

    case "weekly": {
      const daysText = formatWeekdaysList(weeklyRecurrenceDays, t, true);

      return (
        <Trans
          values={{
            days: daysText,
            start: startDate,
            end: endDate,
          }}
        >
          weeklyRepeatingTaskSummaryText2
        </Trans>
      );
    }

    case "monthly":
    default: {
      const recurrenceText = getMonthlyRecurrenceText(
        monthlyRecurrenceType,
        start,
        t
      );

      return (
        <Trans
          values={{
            recurrence: recurrenceText,
            start: startDate,
            end: endDate,
          }}
        >
          monthlyRepeatingTaskSummaryText2
        </Trans>
      );
    }
  }
}

export function getScheduledTaskDescription(
  futureStart: string | null,
  repeatingStart: string | null,
  recurrence: Recurrence | null,
  t: (
    key: string,
    params?: Record<string, string | number | undefined>
  ) => string
) {
  if (futureStart) {
    const startDate = formatDateWithTranslatedMonth(futureStart, t);
    return t("onDateString", { date: startDate });
  }

  if (repeatingStart && recurrence) {
    switch (recurrence.period) {
      case "daily":
        return t("dailyString");

      case "weekly": {
        const { days } = recurrence;
        const daysText = formatWeekdaysList(days, t, true);
        return t("weeklyOnDaysString", { days: daysText });
      }

      case "monthly": {
        const { type } = recurrence;

        const weekdays = getWeekdays(t);
        const startWeekdayNumber = getWeekdayNumber(repeatingStart);
        const startDateWeekday = weekdays.find(
          (day) => day.weekdayNumber === startWeekdayNumber
        );
        return t("monthlyOrdinalOfTheMonthString", {
          ordinal: t(
            type === "last_weekday"
              ? "lastString"
              : calculateWeekdayOrdinalTranslationKey(repeatingStart)
          ),
          weekday: startDateWeekday?.weekday,
        });
      }
    }
  }

  return "";
}

export function getScheduleTypeTitle(
  type: ScheduleType,
  t: (key: string) => string
): string {
  switch (type) {
    case ScheduleType.NO_REPEAT:
      return t("scheduleTaskString");
    case ScheduleType.FUTURE:
      return t("scheduleTaskForFutureString");
    case ScheduleType.REPEATING:
      return t("repeatingTaskString");
  }
}

export function calcIsLastWeekdayOfMonth(date: string) {
  const dayjsDate = dayjs(date);
  return dayjsDate.date() + 7 > dayjsDate.daysInMonth();
}

export function calcIsFifthWeekdayOfMonth(date: string) {
  return (dayjs(date).date() - 1) / 7 >= 4;
}
